import { useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useRecentlyViewed, type RecentlyViewedItem } from '@/hooks/useRecentlyViewed';
import { useCart } from '@/hooks/useCart';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const formatTimeAgo = (ts: number): string => {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин назад`;
  if (hours < 24) return `${hours} ч назад`;
  return `${days} д назад`;
};

const RecentlyViewedCard = ({
  item,
  index,
  visible,
}: {
  item: RecentlyViewedItem;
  index: number;
  visible: boolean;
}) => {
  const { addItem, hasItem } = useCart();
  const inCart = hasItem(item.id);

  return (
    <Link
      to={`/product/${item.id}`}
      className="flex-shrink-0 w-[200px] rounded-2xl overflow-hidden group cursor-pointer flex flex-col relative"
      style={{
        border: `1px solid ${item.color}18`,
        background: 'rgba(255,255,255,0.02)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.96)',
        transition: `opacity 0.5s cubic-bezier(0.22,1,0.36,1) ${index * 70}ms, transform 0.5s cubic-bezier(0.22,1,0.36,1) ${index * 70}ms`,
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.borderColor = `${item.color}45`;
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px) scale(1)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.borderColor = `${item.color}18`;
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0) scale(1)';
      }}
    >
      {/* Discount badge */}
      {item.discount && item.discount > 0 ? (
        <div className="absolute top-2.5 left-2.5 z-10">
          <span
            className="px-2 py-0.5 rounded-full font-orbitron font-black text-[10px]"
            style={{ background: item.color, color: '#050508', boxShadow: `0 0 8px ${item.color}80` }}
          >
            -{item.discount}%
          </span>
        </div>
      ) : null}

      {/* Time badge */}
      <div className="absolute top-2.5 right-2.5 z-10">
        <span
          className="flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[9px]"
          style={{ background: 'rgba(0,0,0,0.55)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <i className="ri-time-line text-[9px]" />
          {formatTimeAgo(item.viewedAt)}
        </span>
      </div>

      {/* Image */}
      <div
        className="relative h-40 flex items-center justify-center overflow-hidden"
        style={{ background: `linear-gradient(135deg, #0a0a14, ${item.color}08)` }}
      >
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-8 rounded-full opacity-15 group-hover:opacity-35 transition-opacity"
          style={{ background: item.color, filter: 'blur(12px)' }}
        />
        <img
          src={item.image}
          alt={item.name}
          className="w-24 h-24 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1"
          style={{ filter: `drop-shadow(0 6px 12px ${item.color}50)` }}
        />
        {/* Hover glow line */}
        <div
          className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: `linear-gradient(90deg, transparent, ${item.color}, transparent)` }}
        />
      </div>

      {/* Info */}
      <div className="p-3.5 flex flex-col flex-1">
        {item.category && (
          <span
            className="font-mono text-[9px] px-2 py-0.5 rounded-full mb-2 self-start"
            style={{ background: `${item.color}12`, color: item.color, border: `1px solid ${item.color}25` }}
          >
            {item.category}
          </span>
        )}

        <h3 className="font-orbitron font-bold text-xs text-white leading-snug mb-2 line-clamp-2 flex-1">
          {item.name}
        </h3>

        {item.rating && (
          <div className="flex items-center gap-1 mb-2.5">
            {[1,2,3,4,5].map(s => (
              <i key={s} className={`text-[9px] ${s <= Math.round(item.rating!) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
            ))}
          </div>
        )}

        {/* Price */}
        <div className="flex items-end justify-between mb-3">
          <div>
            <div
              className="font-orbitron font-black text-base leading-none"
              style={{ color: item.color, textShadow: `0 0 8px ${item.color}60` }}
            >
              {item.price.toLocaleString()}₽
            </div>
            {item.oldPrice && (
              <div className="font-mono text-[10px] text-white/25 line-through mt-0.5">
                {item.oldPrice.toLocaleString()}₽
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            addItem({
              id: item.id,
              name: item.name,
              price: item.price,
              oldPrice: item.oldPrice,
              image: item.image,
              color: item.color,
              category: item.category,
            });
          }}
          className="whitespace-nowrap w-full flex items-center justify-center gap-1.5 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
          style={inCart
            ? { background: item.color, color: '#050508', boxShadow: `0 0 10px ${item.color}80` }
            : { border: `1px solid ${item.color}`, color: item.color }
          }
        >
          <i className={inCart ? 'ri-check-line' : 'ri-shopping-bag-line'} />
          {inCart ? 'В КОРЗИНЕ' : 'В КОРЗИНУ'}
        </button>
      </div>
    </Link>
  );
};

const RecentlyViewed = ({ currentProductId }: { currentProductId: number }) => {
  const { items: allItems, clearAll } = useRecentlyViewed();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.08 });

  const items = useMemo(
    () => allItems.filter(i => i.id !== currentProductId),
    [allItems, currentProductId],
  );

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
  };

  if (items.length === 0) return null;

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-14 px-4 md:px-8 lg:px-12"
      style={{
        background: 'linear-gradient(180deg, #050508 0%, #07070f 100%)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] mb-2" style={{ color: 'rgba(0,245,255,0.5)' }}>
              ИСТОРИЯ ПРОСМОТРОВ
            </p>
            <h2 className="font-orbitron font-black text-2xl md:text-3xl text-white">
              НЕДАВНО{' '}
              <span style={{ color: '#00f5ff', textShadow: '0 0 20px rgba(0,245,255,0.5)' }}>
                ПРОСМОТРЕННЫЕ
              </span>
            </h2>
            <p className="font-mono text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>
              {items.length} {items.length === 1 ? 'товар' : items.length < 5 ? 'товара' : 'товаров'} в истории
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Scroll arrows */}
            <div className="hidden md:flex items-center gap-1.5">
              <button
                onClick={() => scroll('left')}
                className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200"
                style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.2)', color: '#00f5ff' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.18)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.08)'}
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200"
                style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.2)', color: '#00f5ff' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.18)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.08)'}
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>

            {/* Clear history */}
            <button
              onClick={() => clearAll()}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
              style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)';
                (e.currentTarget as HTMLElement).style.color = '#ff2d78';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
                (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.3)';
              }}
            >
              <i className="ri-delete-bin-line text-xs" />
              Очистить
            </button>
          </div>
        </div>

        {/* Scrollable row */}
        <div className="relative">
          {/* Left fade */}
          <div
            className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(90deg, #050508, transparent)' }}
          />
          {/* Right fade */}
          <div
            className="absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(270deg, #050508, transparent)' }}
          />

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-2 px-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {items.map((item, index) => (
              <RecentlyViewedCard
                key={item.id}
                item={item}
                index={index}
                visible={isVisible}
              />
            ))}
          </div>
        </div>

        {/* Bottom hint */}
        <p className="font-mono text-[10px] mt-5 text-center" style={{ color: 'rgba(255,255,255,0.15)' }}>
          История хранится в вашем браузере · Очищается при удалении данных сайта
        </p>
      </div>
    </section>
  );
};

export default RecentlyViewed;
